import React from "react";
import { ImageOff, ArrowRight } from "lucide-react";
import { KIND_META, CONFIDENCE_META } from "./efConsts";

/**
 * EntityList — compact grid list of entity dossier cards.
 * Used as the alternative to the EvidenceBoard graph view.
 *
 * Selection is "soft": clicking selects (drives the inline detail panel)
 * but doesn't navigate — same UX contract as graph nodes.
 */
export const EntityList = ({ nodes = [], edges = [], anchorId, selectedId, onSelectEntity }) => {
  const adjacency = {};
  edges.forEach((e) => {
    (adjacency[e.src_entity_id] ||= 0); adjacency[e.src_entity_id] += 1;
    (adjacency[e.dst_entity_id] ||= 0); adjacency[e.dst_entity_id] += 1;
  });

  // Anchor first, then by connection count desc
  const ordered = [...nodes].sort((a, b) => {
    if (a.id === anchorId) return -1;
    if (b.id === anchorId) return 1;
    return (adjacency[b.id] || 0) - (adjacency[a.id] || 0);
  });

  return (
    <div
      data-testid="ef-entity-list"
      className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3"
    >
      {ordered.map((e) => {
        const meta = KIND_META[e.kind] || {};
        const cm = CONFIDENCE_META[e.confidence_level] || {};
        const isAnchor = e.id === anchorId;
        const isSelected = e.id === selectedId;
        const conn = adjacency[e.id] || 0;
        return (
          <button
            key={e.id}
            onClick={() => onSelectEntity?.(e)}
            data-testid={`ef-list-card-${e.id}`}
            className={
              "group text-left border bg-[#0a0907] hover:border-[#E0A526]/70 transition-colors flex gap-3 p-3 " +
              (isSelected ? "border-[#E0A526] shadow-[0_0_0_2px_rgba(224,165,38,0.30)]" : "border-[#2D2A26]")
            }
          >
            {/* Thumb */}
            <div className="w-14 h-16 border border-[#2D2A26] bg-[#0F0F0F] shrink-0 relative overflow-hidden">
              {(e.thumbnail_url || e.photo_url || e.image_url) ? (
                <img
                  src={e.thumbnail_url || e.photo_url || e.image_url}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={(ev) => { ev.currentTarget.style.display = "none"; }}
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center text-[#454038]">
                  <ImageOff size={20} strokeWidth={1.2} />
                </div>
              )}
              {isAnchor && (
                <div className="absolute top-0.5 left-0.5 font-mono text-[7px] uppercase tracking-[0.2em] text-[#0F0F0F] bg-[#D92D20] px-1">
                  A
                </div>
              )}
            </div>

            {/* Body */}
            <div className="min-w-0 flex-1 flex flex-col">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="font-mono text-[8px] uppercase tracking-[0.25em] text-[#757065]">
                  {meta.label || (e.kind || "—").toUpperCase()}
                </span>
                {e.confidence_level && (
                  <span
                    style={{ color: cm.color }}
                    className="font-mono text-[8px] uppercase tracking-[0.25em] ml-auto"
                  >
                    {cm.label}
                  </span>
                )}
              </div>
              <div className="font-heading text-sm text-[#E4DFD3] leading-tight line-clamp-1 group-hover:text-white">
                {e.name}
              </div>
              {e.category_label && (
                <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#A39E93] mt-0.5 line-clamp-1">
                  {e.category_label}
                </div>
              )}
              {e.connection_summary_to_epstein && (
                <div className="text-[11px] text-[#A39E93] line-clamp-2 mt-1">
                  {e.connection_summary_to_epstein}
                </div>
              )}
              <div className="mt-auto pt-1 flex items-center justify-between font-mono text-[9px] uppercase tracking-[0.25em] text-[#5a544a]">
                <span>{conn} conn · {(e.source_refs || []).length} src</span>
                <ArrowRight size={10} className="opacity-0 group-hover:opacity-100 text-[#D92D20]" />
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

export default EntityList;
